import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { SlidersHorizontal } from 'lucide-react';

export interface TimeFilters {
  statuses?: string[];       // 'approved' | 'pending'
  billable?: string[];       // 'billable' | 'non-billable'
  employees?: string[];      // employeeId as string
  roles?: string[];          // roleId values
  costCentres?: string[];    // costCentreId values
  workTypes?: string[];      // workTypeId values
  dateFrom?: string;
  dateTo?: string;
  hoursMin?: string;
  hoursMax?: string;
}

interface TimeEntriesFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TimeFilters;
  onApply: (filters: TimeFilters) => void;
  onClear: () => void;
  availableEmployees: Array<{ value: string; label: string }>;
  availableRoles?: Array<{ value: string; label: string }>;
  availableCostCentres?: Array<{ value: string; label: string }>;
  availableWorkTypes?: Array<{ value: string; label: string }>;
}

export function countActiveTimeFilters(filters: TimeFilters): number {
  let count = 0;
  if (filters.statuses?.length) count++;
  if (filters.billable?.length) count++;
  if (filters.employees?.length) count++;
  if (filters.roles?.length) count++;
  if (filters.costCentres?.length) count++;
  if (filters.workTypes?.length) count++;
  if (filters.dateFrom || filters.dateTo) count++;
  if (filters.hoursMin || filters.hoursMax) count++;
  return count;
}

export function applyTimeFilters<T extends {
  approved: boolean;
  billable: boolean;
  employeeId: number;
  date: string;
  hours: number;
  roleId?: string | null;
  costCentreId?: string | null;
  workTypeId?: string | null;
}>(items: T[], filters: TimeFilters): T[] {
  return items.filter((item) => {
    if (filters.statuses?.length) {
      const status = item.approved ? 'approved' : 'pending';
      if (!filters.statuses.includes(status)) return false;
    }
    if (filters.billable?.length) {
      const bStatus = item.billable ? 'billable' : 'non-billable';
      if (!filters.billable.includes(bStatus)) return false;
    }
    if (filters.employees?.length) {
      if (!filters.employees.includes(String(item.employeeId))) return false;
    }
    if (filters.roles?.length) {
      if (!item.roleId || !filters.roles.includes(item.roleId)) return false;
    }
    if (filters.costCentres?.length) {
      if (!item.costCentreId || !filters.costCentres.includes(item.costCentreId)) return false;
    }
    if (filters.workTypes?.length) {
      if (!item.workTypeId || !filters.workTypes.includes(item.workTypeId)) return false;
    }
    if (filters.dateFrom && item.date < filters.dateFrom) return false;
    if (filters.dateTo && item.date > filters.dateTo) return false;
    if (filters.hoursMin && item.hours < Number(filters.hoursMin)) return false;
    if (filters.hoursMax && item.hours > Number(filters.hoursMax)) return false;
    return true;
  });
}

const STATUS_OPTIONS = [
  { value: 'approved', label: 'Approved' },
  { value: 'pending',  label: 'Pending' },
];

const BILLABLE_OPTIONS = [
  { value: 'billable',     label: 'Billable' },
  { value: 'non-billable', label: 'Non-Billable' },
];

/** Maximum absolute value for numeric filter inputs (prevents scientific notation overflow) */
const NUMERIC_FILTER_MAX_VALUE = 999_999_999_999;

export function TimeEntriesFilterSheet({
  open,
  onOpenChange,
  filters,
  onApply,
  onClear,
  availableEmployees,
  availableRoles = [],
  availableCostCentres = [],
  availableWorkTypes = [],
}: TimeEntriesFilterSheetProps) {
  const [draft, setDraft] = useState<TimeFilters>(filters);

  // Sync draft whenever the sheet opens or external filters change
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const set = <K extends keyof TimeFilters>(key: K, value: TimeFilters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const handleClear = () => {
    setDraft({});
    onClear();
    onOpenChange(false);
  };

  const activeCount = countActiveTimeFilters(draft);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        style={{ maxWidth: 520, width: '100%' }}
        className="sm:max-w-[560px] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
            <SheetTitle>Advanced Filters</SheetTitle>
            {activeCount > 0 && (
              <Badge variant="default" className="ml-auto text-xs">
                {activeCount} active
              </Badge>
            )}
          </div>
          <SheetDescription>
            Narrow down time entries using the filters below.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Status */}
          <div className="space-y-2">
            <Label>Approval Status</Label>
            <MultiSelect
              value={draft.statuses ?? []}
              onValueChange={(v) => set('statuses', v)}
              options={STATUS_OPTIONS}
              placeholder="All statuses"
              emptyMessage="No statuses found."
            />
          </div>

          {/* Billable */}
          <div className="space-y-2">
            <Label>Billable Type</Label>
            <MultiSelect
              value={draft.billable ?? []}
              onValueChange={(v) => set('billable', v)}
              options={BILLABLE_OPTIONS}
              placeholder="All types"
              emptyMessage="No options found."
            />
          </div>

          {/* Employee */}
          <div className="space-y-2">
            <Label>Employee</Label>
            <MultiSelect
              value={draft.employees ?? []}
              onValueChange={(v) => set('employees', v)}
              options={availableEmployees}
              placeholder="All employees"
              emptyMessage="No employees found."
              maxDisplay={3}
            />
          </div>

          {/* Role */}
          {availableRoles.length > 0 && (
            <div className="space-y-2">
              <Label>Role</Label>
              <MultiSelect
                value={draft.roles ?? []}
                onValueChange={(v) => set('roles', v)}
                options={availableRoles}
                placeholder="All roles"
                emptyMessage="No roles found."
                maxDisplay={3}
              />
            </div>
          )}

          {/* Work Type */}
          {availableWorkTypes.length > 0 && (
            <div className="space-y-2">
              <Label>Work Type</Label>
              <MultiSelect
                value={draft.workTypes ?? []}
                onValueChange={(v) => set('workTypes', v)}
                options={availableWorkTypes}
                placeholder="All work types"
                emptyMessage="No work types found."
                maxDisplay={3}
              />
            </div>
          )}

          {/* Cost Centre */}
          {availableCostCentres.length > 0 && (
            <div className="space-y-2">
              <Label>Cost Centre</Label>
              <MultiSelect
                value={draft.costCentres ?? []}
                onValueChange={(v) => set('costCentres', v)}
                options={availableCostCentres}
                placeholder="All cost centres"
                emptyMessage="No cost centres found."
                maxDisplay={3}
              />
            </div>
          )}

          {/* Date Range */}
          <div className="space-y-2">
            <Label>Date Range</Label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={draft.dateFrom ?? ''}
                onChange={(e) => set('dateFrom', e.target.value || undefined)}
                className="flex-1"
              />
              <span className="text-muted-foreground text-sm shrink-0">to</span>
              <Input
                type="date"
                value={draft.dateTo ?? ''}
                onChange={(e) => set('dateTo', e.target.value || undefined)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Hours Range */}
          <div className="space-y-2">
            <Label>Hours Range</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                step={0.5}
                placeholder="Min hrs"
                value={draft.hoursMin ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw !== '' && (!isFinite(Number(raw)) || Math.abs(Number(raw)) > NUMERIC_FILTER_MAX_VALUE)) return;
                  set('hoursMin', raw || undefined);
                }}
                className="flex-1"
              />
              <span className="text-muted-foreground text-sm shrink-0">–</span>
              <Input
                type="number"
                min={0}
                step={0.5}
                placeholder="Max hrs"
                value={draft.hoursMax ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw !== '' && (!isFinite(Number(raw)) || Math.abs(Number(raw)) > NUMERIC_FILTER_MAX_VALUE)) return;
                  set('hoursMax', raw || undefined);
                }}
                className="flex-1"
              />
            </div>
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t flex flex-row gap-2">
          <Button variant="outline" onClick={handleClear} className="flex-1">
            Clear All
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Apply Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
